import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import _ from 'lodash';

const ParticleBackground = () => {
  const canvasRef = useRef(null);
  const theme = useTheme();
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;
    
    // Create award icons
    const icons = [
      { type: 'trophy', emoji: '🏆', size: 24 },
      { type: 'star', emoji: '⭐', size: 20 },
      { type: 'medal', emoji: '🏅', size: 22 }
    ];
    
    // Create particles using Lodash
    const particleCount = 25;
    const newParticles = _.times(particleCount, () => {
      const icon = _.sample(icons);
      return {
        x: _.random(0, width),
        y: _.random(0, height),
        size: icon.size,
        speedX: _.random(-0.15, 0.15, true),
        speedY: _.random(-0.15, 0.15, true),
        rotation: _.random(0, 360),
        rotationSpeed: _.random(-0.25, 0.25, true),
        emoji: icon.emoji,
        opacity: theme.palette.mode === 'dark'
          ? _.random(0.4, 0.7, true) // Dark mode opacity
          : _.random(0.3, 0.7, true)  // Light mode opacity
      };
    });

    // Animation function
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      newParticles.forEach(particle => {
        // Update particle position
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        particle.rotation += particle.rotationSpeed;
        
        // Wrap around edges with Lodash clamp
        if (particle.x < -particle.size) particle.x = width + particle.size;
        if (particle.x > width + particle.size) particle.x = -particle.size;
        if (particle.y < -particle.size) particle.y = height + particle.size;
        if (particle.y > height + particle.size) particle.y = -particle.size;
        
        // Save the current context state
        ctx.save();
        
        // Add glow effect
        ctx.shadowBlur = 10;
        ctx.shadowColor = theme.palette.mode === 'dark'
          ? `rgba(255, 255, 255, ${particle.opacity})`
          : `rgba(0, 0, 0, ${particle.opacity * 0.6})`;
        
        // Translate to particle position
        ctx.translate(particle.x, particle.y);
        
        // Rotate
        ctx.rotate(particle.rotation * Math.PI / 180);
        
        // Set opacity
        ctx.globalAlpha = particle.opacity;
        
        // Draw emoji with increased size for better visibility
        ctx.font = `${particle.size * 1.2}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(particle.emoji, 0, 0);
        
        // Restore the context state
        ctx.restore();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    // Handle resize with debounce
    const handleResize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [theme.palette.mode]);
  
  return (
    <canvas 
      ref={canvasRef} 
      style={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        width: '100%', 
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0
      }} 
    />
  );
};

export default ParticleBackground; 